import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Search, Trash2, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth, formatApiError } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const truncate = (s, n = 80) => {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const ActivityTable = ({
  title,
  testIdPrefix,
  endpoint, // "subscribers" or "contact"
  columns, // [{ key, label, render? }]
  searchPlaceholder = "Search by email…",
  emptyText = "Nothing here yet.",
  itemLabel = "row",
}) => {
  const { getToken } = useAuth();

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounced(searchInput, 300);

  const [data, setData] = useState({ items: [], total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // row to delete
  const [deleting, setDeleting] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken]
  );

  // Single fetch effect — derives everything from page / search / refetchKey
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const params = { page, limit };
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
        const { data: res } = await axios.get(`${API}/admin/${endpoint}`, {
          ...auth,
          params,
        });
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            formatApiError(err?.response?.data?.detail) ||
              "Could not load data."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [endpoint, page, limit, debouncedSearch, auth, refetchKey]);

  // Search change → snap back to page 1 (do this in the change handler, not in an effect)
  const onSearchChange = (e) => {
    setSearchInput(e.target.value);
    setPage(1);
  };

  const handleDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/${endpoint}/${confirm.id}`, auth);
      toast.success(`${itemLabel} deleted.`);
      setConfirm(null);
      // If we just deleted the last row on the page, go back a page
      if (data.items.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        setRefetchKey((k) => k + 1);
      }
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Delete failed."
      );
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = data.pages || 0;
  const showingFrom = data.total === 0 ? 0 : (page - 1) * limit + 1;
  const showingTo = Math.min(page * limit, data.total);

  return (
    <section
      data-testid={`${testIdPrefix}-table-section`}
      className="bg-white border border-[#E7E7E2] rounded-2xl overflow-hidden"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-7 py-5 border-b border-[#E7E7E2]">
        <div>
          <h3 className="font-sora text-[20px] md:text-[22px] font-semibold text-[#111111] tracking-tight">
            {title}
          </h3>
          <p className="mt-1 font-mono text-[11px] tracking-[0.16em] uppercase text-[#111111]/45">
            {data.total} total · showing {showingFrom}–{showingTo}
          </p>
        </div>

        <div className="relative w-full sm:w-72">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#111111]/40"
          />
          <input
            type="text"
            value={searchInput}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
            data-testid={`${testIdPrefix}-search-input`}
            className="w-full bg-[#F7F7F4] border border-[#E7E7E2] rounded-lg pl-9 pr-9 py-2.5 text-[14px] text-[#111111] placeholder:text-[#111111]/40 font-manrope focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15 transition-all"
          />
          {searchInput && (
            <button
              onClick={() => {
                setSearchInput("");
                setPage(1);
              }}
              data-testid={`${testIdPrefix}-search-clear`}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#111111]/40 hover:text-[#111111] p-1"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table
          data-testid={`${testIdPrefix}-table`}
          className="w-full font-manrope text-[14px]"
        >
          <thead>
            <tr className="bg-[#F7F7F4] text-left">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="px-5 py-3 font-mono text-[10px] tracking-[0.18em] uppercase text-[#111111]/55 font-semibold border-b border-[#E7E7E2]"
                >
                  {c.label}
                </th>
              ))}
              <th className="px-5 py-3 border-b border-[#E7E7E2] w-12"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="px-5 py-10 text-center text-[#111111]/50 font-mono text-[12px] tracking-wider uppercase"
                >
                  Loading…
                </td>
              </tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  data-testid={`${testIdPrefix}-empty`}
                  className="px-5 py-10 text-center text-[#111111]/55 font-manrope text-[14px]"
                >
                  {emptyText}
                </td>
              </tr>
            )}
            {!loading &&
              data.items.map((row, i) => (
                <tr
                  key={row.id}
                  data-testid={`${testIdPrefix}-row-${i}`}
                  className="border-b border-[#E7E7E2] last:border-b-0 hover:bg-[#F7F7F4]/60 transition-colors"
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className="px-5 py-4 align-top text-[#111111]/85"
                    >
                      {c.render ? c.render(row) : row[c.key]}
                    </td>
                  ))}
                  <td className="px-5 py-4 text-right">
                    <button
                      onClick={() => setConfirm(row)}
                      data-testid={`${testIdPrefix}-delete-${i}`}
                      aria-label="Delete row"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[#111111]/45 hover:text-[#2563EB] hover:bg-[#2563EB]/10 transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-7 py-4 border-t border-[#E7E7E2] bg-[#F7F7F4]/50">
        <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#111111]/55">
          Page {totalPages === 0 ? 0 : page} / {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <button
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid={`${testIdPrefix}-prev`}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-[#E7E7E2] text-[13px] font-manrope text-[#111111] bg-white hover:border-[#111111] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={14} />
            Prev
          </button>
          <button
            disabled={page >= totalPages || loading || totalPages === 0}
            onClick={() => setPage((p) => p + 1)}
            data-testid={`${testIdPrefix}-next`}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-[#E7E7E2] text-[13px] font-manrope text-[#111111] bg-white hover:border-[#111111] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Delete confirm dialog */}
      <AlertDialog
        open={!!confirm}
        onOpenChange={(open) => !open && !deleting && setConfirm(null)}
      >
        <AlertDialogContent
          data-testid={`${testIdPrefix}-delete-confirm`}
          className="bg-white border border-[#E7E7E2] rounded-2xl"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-sora text-[22px] font-semibold text-[#111111] tracking-tight">
              Delete this {itemLabel}?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-manrope text-[14px] text-[#111111]/65 leading-relaxed">
              {confirm?.email ? (
                <>
                  <span className="block">
                    <strong className="text-[#111111]">{confirm.email}</strong>
                  </span>
                  <span className="block mt-2">
                    This action is permanent and cannot be undone.
                  </span>
                </>
              ) : (
                <>This action is permanent and cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              data-testid={`${testIdPrefix}-delete-cancel`}
              className="bg-white border border-[#E7E7E2] text-[#111111] hover:bg-[#F7F7F4] font-manrope font-medium rounded-lg"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              data-testid={`${testIdPrefix}-delete-confirm-btn`}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium rounded-lg"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

// ---- Specialised tables ----

const SUBSCRIBER_COLUMNS = [
  {
    key: "email",
    label: "Email",
    render: (r) => <span className="text-[#111111]">{r.email}</span>,
  },
  {
    key: "source",
    label: "Source",
    render: (r) => (
      <span className="font-mono text-[12px] text-[#111111]/65">
        {r.source || "—"}
      </span>
    ),
  },
  {
    key: "created_at",
    label: "Joined",
    render: (r) => (
      <span className="text-[#111111]/65 whitespace-nowrap">
        {formatDate(r.created_at)}
      </span>
    ),
  },
];

const CONTACT_COLUMNS = [
  {
    key: "name",
    label: "From",
    render: (r) => (
      <div>
        <div className="text-[#111111] font-medium">{r.name}</div>
        <div className="text-[#111111]/55 text-[12.5px] mt-0.5">
          {r.email}
        </div>
      </div>
    ),
  },
  {
    key: "message",
    label: "Message",
    render: (r) => (
      <span className="text-[#111111]/75 block max-w-md">
        {truncate(r.message, 140)}
      </span>
    ),
  },
  {
    key: "created_at",
    label: "Received",
    render: (r) => (
      <span className="text-[#111111]/65 whitespace-nowrap">
        {formatDate(r.created_at)}
      </span>
    ),
  },
];

export const SubscribersTable = () => (
  <ActivityTable
    title="Subscribers"
    testIdPrefix="subscribers"
    endpoint="subscribers"
    itemLabel="subscriber"
    emptyText="No subscribers match your search."
    searchPlaceholder="Search by email or source…"
    columns={SUBSCRIBER_COLUMNS}
  />
);

export const ContactTable = () => (
  <ActivityTable
    title="Contact messages"
    testIdPrefix="contact"
    endpoint="contact"
    itemLabel="message"
    emptyText="No messages match your search."
    searchPlaceholder="Search by email, name, or message…"
    columns={CONTACT_COLUMNS}
  />
);

export default ActivityTable;
